import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { configureWebCore } from '@taskydo/web-core/runtime.js';

import AppBanner from '@taskydo/web-core/AppBanner.jsx';
import App from './App.jsx';
import * as appConfig from './config.js';
import { AuthProvider } from './lib/auth.jsx';
import { NotificationProvider, Toaster, notify } from './lib/notifications.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { formatApiError } from './lib/errors.js';
import { registerServiceWorker } from './lib/serviceWorker.js';
import './styles/global.css';

// PWA boot — installs the offline-first cache + Background Sync hook
// for the Pharma SFA module's offline DCR queue.  No-op in dev unless
// VITE_ENABLE_SW=1.  See ``src/lib/serviceWorker.js``.
registerServiceWorker();

// Shared handoff and the app switcher read storage keys from this slot.
configureWebCore(appConfig);

// ---------------------------------------------------------------------------
// Surface JS-level errors that React itself can't catch.
//
// React's <ErrorBoundary> only sees errors thrown during *render*.  Errors
// in event handlers, setTimeout callbacks, or an `await` that nobody
// awaited would otherwise vanish.  Listening to `error` and
// `unhandledrejection` makes them visible too — exactly what the brief
// asked for.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    // eslint-disable-next-line no-console
    console.error('[window.onerror]', e.error || e.message);
    notify.error(
      (e.error && (e.error.message || String(e.error))) || e.message || 'Script error',
      { title: 'Unhandled error' },
    );
  });

  window.addEventListener('unhandledrejection', (e) => {
    // eslint-disable-next-line no-console
    console.error('[unhandledrejection]', e.reason);
    notify.error(formatApiError(e.reason), { title: 'Unhandled rejection' });
  });
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <NotificationProvider>
        <ErrorBoundary>
          <AuthProvider>
            <AppBanner />
            <App />
          </AuthProvider>
        </ErrorBoundary>
        {/* Toaster sits OUTSIDE the error boundary so it can display
            messages even if the rest of the tree has crashed. */}
        <Toaster />
      </NotificationProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
